import React from "react";
import { Box, Typography, Paper, Grid } from "@mui/material";
import TableSection from "../TableSection";
import { BarChart } from "../Charts";
import { formatBRL, formatNumber } from "../utils/formatters";

export default function PrecosReceitaTab({
  averagePrice,
  salesByPriceRange,
  priceVolumeCorrelation
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Resumo de preços */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1a237e' }}>Preço Médio Catálogo</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {averagePrice && averagePrice[0] ? formatBRL(averagePrice[0].averagePrice) : "-"}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Mínimo</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {averagePrice && averagePrice[0] ? formatBRL(averagePrice[0].minPrice) : "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Máximo</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {averagePrice && averagePrice[0] ? formatBRL(averagePrice[0].maxPrice) : "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">SKUs</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {averagePrice && averagePrice[0] ? averagePrice[0].count : "-"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Distribuição por faixa de preço */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1a237e' }}>
          Distribuição de Vendas por Faixa de Preço
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#607d8b' }}>
          Visualize o volume de vendas em diferentes faixas de preço para identificar oportunidades de precificação e demanda.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {salesByPriceRange && salesByPriceRange.length > 0 ? (
              <TableSection
                showTitle={false}
                columns={[
                  { field: "_id", headerName: "Faixa de Preço" }, 
                  { field: "count", headerName: "Vendas", format: (v) => formatNumber(v, 0) }, 
                  { field: "totalRevenue", headerName: "Receita", format: (v) => formatBRL(v) }
                ]}
                rows={salesByPriceRange || []}
                emptyMessage="Nenhum dado disponível"
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <Typography variant="body1" color="text.secondary">Nenhum dado disponível para o período selecionado</Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {salesByPriceRange && salesByPriceRange.length > 0 ? (
              <Box sx={{ width: '100%', height: 300 }}>
                <BarChart
                  xAxis={[{ 
                    scaleType: "band", 
                    data: salesByPriceRange.map((r) => r._id),
                    label: 'Faixa de Preço'
                  }]}
                  series={[{ 
                    data: salesByPriceRange.map((r) => r.count), 
                    label: "Vendas",
                    color: '#1a237e'
                  }]}
                  width={undefined}
                  height={280}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                <Typography variant="body1" color="text.secondary">Nenhum dado disponível para gerar o gráfico</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
      
      {/* Correlação preço x volume */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1a237e' }}>
          Correlação Preço x Volume de Vendas
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#607d8b' }}>
          Entenda como diferentes faixas de preço impactam o volume de vendas em cada categoria.
        </Typography>
        
        {priceVolumeCorrelation && priceVolumeCorrelation.length > 0 ? (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Categoria: {priceVolumeCorrelation[0].categoryName || "-"}
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 350 }}>
              <BarChart
                xAxis={[{ 
                  scaleType: "band", 
                  data: priceVolumeCorrelation[0].priceRanges.map((r) => r.priceRange),
                  label: 'Faixa de Preço'
                }]}
                series={[
                  { 
                    data: priceVolumeCorrelation[0].priceRanges.map((r) => r.totalSales), 
                    label: "Vendas",
                    color: '#1a237e'
                  },
                  { 
                    data: priceVolumeCorrelation[0].priceRanges.map((r) => r.averagePrice/10), 
                    label: "Preço Médio (R$ ÷ 10)",
                    color: '#f57c00'
                  }
                ]}
                width={undefined}
                height={300}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography variant="body1" color="text.secondary">Nenhum dado disponível para o período selecionado</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
